import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import AdminLayout from '../components/AdminLayout';
import { Plus, Edit, Trash2, User, Phone, Mail, CheckCircle, XCircle, ChevronDown, Eye, EyeOff, Lock, RefreshCw, X } from 'lucide-react';

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    emergency_phone: '',
    address: '',
    aadhar_card: '',
    bank_account_number: '',
    bank_ifsc_code: '',
    bank_name: '',
    pan_card: '',
    is_active: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({});

  // Change Password modal state
  const [showChangePwdModal, setShowChangePwdModal] = useState(false);
  const [changePwdEmployee, setChangePwdEmployee] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [isChangingPwd, setIsChangingPwd] = useState(false);

  const togglePasswordVisibility = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openChangePwdModal = (employee) => {
    setChangePwdEmployee(employee);
    setNewPassword('');
    setShowNewPwd(false);
    setShowChangePwdModal(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.trim().length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setIsChangingPwd(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/admin/salespersons/${changePwdEmployee.id}/password`, { newPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Password updated for ${changePwdEmployee.name}`);
      setShowChangePwdModal(false);
      setChangePwdEmployee(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update password');
    } finally {
      setIsChangingPwd(false);
    }
  };

  const generateNewPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let pwd = '';
    for (let i = 0; i < 12; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    setNewPassword(pwd);
    setShowNewPwd(true);
  };

  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    if (!dataFetched) {
      setDataFetched(true);
      fetchEmployees();
    }
  }, [dataFetched]);

  const fetchEmployees = async (retryCount = 0) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/admin/salespersons', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);

      // Handle 429 with retry logic
      if (error.response?.status === 429 && retryCount < 1) {
        setTimeout(() => {
          fetchEmployees(retryCount + 1);
        }, 1000);
        return;
      }

      // Use fallback sample data
      const sampleEmployees = [
        { id: 1, name: 'John Doe', email: 'john@example.com', phone: '+91 9876543210', role: 'Sales Manager' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com', phone: '+91 9876543211', role: 'Sales Executive' }
      ];
      setEmployees(sampleEmployees);

      if (error.response?.status === 429) {
        toast('Server is busy, using sample data', { icon: '⚠️' });
      } else {
        toast('Using sample data - API connection failed', { icon: '⚠️' });
      }
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setGeneratedPassword(password);
    setFormData({ ...formData, password });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast.success('Password copied to clipboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate password length if provided
    if (formData.password && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      // Focus on password field
      document.querySelector('input[type="password"]')?.focus();
      return;
    }

    // Validate password for new employees
    if (!editingEmployee && !formData.password) {
      toast.error('Please generate or enter a password for the new employee');
      // Focus on password field
      document.querySelector('input[type="password"]')?.focus();
      return;
    }

    try {
      const token = localStorage.getItem('token');

      if (editingEmployee) {
        // For editing, don't send password unless it's changed
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await axios.put(`/admin/salespersons/${editingEmployee.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee updated successfully');
      } else {
        // For new employee, password is required
        // Strip empty optional fields so the backend doesn't receive empty strings
        const payload = { ...formData };
        if (!payload.email) delete payload.email;  // email is optional
        await axios.post('/admin/salespersons', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee created successfully! Share the credentials privately.');
      }

      setShowAddModal(false);
      setEditingEmployee(null);
      setFormData({ name: '', phone: '', email: '', password: '', is_active: true });
      setGeneratedPassword('');
      fetchEmployees();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Operation failed';
      toast.error(errorMessage);
      console.error('Error:', error);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      phone: employee.phone,
      email: employee.email || '',
      password: employee.password || '',
      emergency_phone: employee.emergency_phone || '',
      address: employee.address || '',
      aadhar_card: employee.aadhar_card || '',
      bank_account_number: employee.bank_account_number || '',
      bank_ifsc_code: employee.bank_ifsc_code || '',
      bank_name: employee.bank_name || '',
      pan_card: employee.pan_card || '',
      is_active: employee.is_active !== false
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/admin/salespersons/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee deleted successfully');
        fetchEmployees();
      } catch (error) {
        toast.error('Failed to delete employee');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      password: '',
      emergency_phone: '',
      address: '',
      aadhar_card: '',
      bank_account_number: '',
      bank_ifsc_code: '',
      bank_name: '',
      pan_card: '',
      is_active: true
    });
    setGeneratedPassword('');
    setEditingEmployee(null);
    setShowAddModal(false);
  };

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 py-8">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A5F38]">Employee Management</h1>
          <p className="text-gray-600">Manage your sales team members</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-[#C5A57D] text-[#0A5F38] px-6 py-2.5 rounded-lg font-bold hover:bg-[#B8985E] transition-colors flex items-center shadow-sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Employee
        </button>
      </div>

      {/* Employees Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {(() => {
          // Calculate pagination
          const startIndex = (currentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          const paginatedEmployees = employees.slice(startIndex, endIndex);

          return paginatedEmployees.map((employee) => (
            <div key={employee.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="bg-slate-100 p-2.5 rounded-full">
                    <User className="h-6 w-6 text-slate-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-[#0A5F38]">{employee.name}</h3>
                    <p className="text-sm text-gray-500">ID: {employee.salesperson_id}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  {employee.is_active ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2 text-[#C5A57D]" />
                  {employee.phone}
                </div>
                {employee.email && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="h-4 w-4 mr-2 text-[#C5A57D]" />
                    {employee.email}
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(employee)}
                  className="bg-[#C5A57D] text-[#0A5F38] px-4 py-2 rounded-lg font-bold hover:bg-[#B8985E] transition-colors flex-1 flex items-center justify-center text-sm"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => openChangePwdModal(employee)}
                  className="bg-[#0A5F38] text-white px-3 py-2 rounded-lg hover:bg-[#084d2d] transition-colors flex items-center justify-center"
                  title="Change Password"
                >
                  <Lock className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(employee.id)}
                  className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ));
        })()}
      </div>

      {/* Pagination Controls */}
      {employees.length > itemsPerPage && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-slate-100 sm:px-6 rounded-lg mt-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(Math.ceil(employees.length / itemsPerPage), currentPage + 1))}
              disabled={currentPage === Math.ceil(employees.length / itemsPerPage)}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {Math.min((currentPage - 1) * itemsPerPage + 1, employees.length)}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, employees.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{employees.length}</span>{' '}
                results
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value={6}>6</option>
                <option value={9}>9</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
              </select>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronDown className="h-5 w-5 rotate-90" />
                </button>

                {/* Page Numbers */}
                {(() => {
                  const totalPages = Math.ceil(employees.length / itemsPerPage);
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i
                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-white'
                          }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}

                <button
                  onClick={() => setCurrentPage(Math.min(Math.ceil(employees.length / itemsPerPage), currentPage + 1))}
                  disabled={currentPage === Math.ceil(employees.length / itemsPerPage)}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronDown className="h-5 w-5 -rotate-90" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {employees.length === 0 && (
        <div className="text-center py-12">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No employees found</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first employee</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary"
          >
            Add Employee
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-[#F8F5E6] rounded-2xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto shadow-2xl border border-[#C5A57D]/20 scrollbar-thin scrollbar-thumb-[#0A5F38] scrollbar-track-gray-100">
            <h2 className="text-xl font-bold text-[#0A5F38] mb-4">
              {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                  placeholder="Enter employee name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone (for login) *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, phone: val });
                  }}
                  className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                  placeholder="Enter 10-digit phone number"
                  pattern="[0-9]{10}"
                  title="Phone number must be 10 digits"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                  placeholder="Enter email address"
                />
                <p className="text-xs text-slate-500 mt-1">Optional if Phone is provided</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {editingEmployee ? 'Change Password' : 'Password *'}
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required={!editingEmployee}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                      placeholder={editingEmployee ? "Leave blank to keep unchanged" : "Enter password"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="bg-[#0A5F38] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#084d2d] transition"
                  >
                    Generate
                  </button>
                  {formData.password && (
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(formData.password);
                        toast.success('Password copied to clipboard');
                      }}
                      className="bg-[#C5A57D] text-[#0A5F38] px-3 py-2 rounded-lg font-semibold hover:bg-[#B8985E] transition"
                      title="Copy to clipboard"
                      layout-id="copy-pwd-btn"
                    >
                      Copy
                    </button>
                  )}
                </div>
                {formData.password ? (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ Password set ({formData.password.length} characters)
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-slate-500">
                    {editingEmployee ? 'Leave blank to keep the current password' : 'Password is required'}
                  </p>
                )}
              </div>

              {/* Additional Employee Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Emergency Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.emergency_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    placeholder="Enter emergency contact number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    placeholder="Enter address"
                    rows="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Aadhar Card Number
                  </label>
                  <input
                    type="text"
                    value={formData.aadhar_card}
                    onChange={(e) => setFormData({ ...formData, aadhar_card: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    placeholder="Enter Aadhar card number (16 digits)"
                    maxLength="16"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    PAN Card Number
                  </label>
                  <input
                    type="text"
                    value={formData.pan_card}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      // PAN format: 5 letters, 4 digits, 1 letter (total 10 chars)
                      const panRegex = /^[A-Z]{0,5}[0-9]{0,4}[A-Z]{0,1}$/;
                      if (panRegex.test(value) || value === '') {
                        setFormData({ ...formData, pan_card: value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    placeholder="Enter PAN card number (e.g., AAAAA1234A)"
                    maxLength="10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Bank Account Number
                  </label>
                  <input
                    type="text"
                    value={formData.bank_account_number}
                    onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    placeholder="Enter bank account number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    placeholder="Enter bank name"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Bank IFSC Code
                  </label>
                  <input
                    type="text"
                    value={formData.bank_ifsc_code}
                    onChange={(e) => setFormData({ ...formData, bank_ifsc_code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white"
                    placeholder="Enter bank IFSC code"
                    maxLength="11"
                  />
                </div>
              </div>

              {editingEmployee && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-[#0A5F38] rounded border-slate-300 focus:ring-[#0A5F38]"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-slate-700">
                    Active
                  </label>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-white border border-[#C5A57D]/30 text-slate-700 px-6 py-2.5 rounded-lg font-medium hover:bg-[#F3EFE0] transition-colors flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#C5A57D] text-[#0A5F38] px-6 py-2.5 rounded-lg font-bold hover:bg-[#B8985E] transition-colors flex-1"
                >
                  {editingEmployee ? 'Update' : 'Add'} Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePwdModal && changePwdEmployee && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#F8F5E6] rounded-2xl w-full max-w-sm shadow-2xl border border-[#C5A57D]/30 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0A5F38] to-[#0d7a48] px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-white/20 rounded-lg p-1.5">
                  <Lock className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">Change Password</h3>
                  <p className="text-white/70 text-xs">{changePwdEmployee.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowChangePwdModal(false)}
                className="text-white/70 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleChangePassword} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#0A5F38] uppercase tracking-wide mb-1.5">
                  New Password
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showNewPwd ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 pr-9 border border-[#C5A57D]/30 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white text-sm"
                      placeholder="Enter new password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPwd(!showNewPwd)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={generateNewPassword}
                    className="bg-[#0A5F38] text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-[#084d2d] transition whitespace-nowrap"
                  >
                    Generate
                  </button>
                </div>
                {newPassword && (
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-green-600">✓ {newPassword.length} characters</p>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(newPassword); toast.success('Password copied!'); }}
                      className="text-xs text-[#C5A57D] hover:text-[#B8985E] font-medium"
                    >
                      Copy
                    </button>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-1">Minimum 6 characters required</p>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowChangePwdModal(false)}
                  className="flex-1 bg-white border border-[#C5A57D]/30 text-slate-700 py-2 rounded-lg text-sm font-medium hover:bg-[#F3EFE0] transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isChangingPwd || !newPassword.trim()}
                  className="flex-1 bg-[#0A5F38] text-white py-2 rounded-lg text-sm font-bold hover:bg-[#084d2d] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                >
                  {isChangingPwd ? (
                    <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Saving...</>
                  ) : (
                    <><Lock className="h-3.5 w-3.5" /> Update Password</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;


